import { useState, useEffect, useCallback } from 'react';
import { useI18n, useNamespace } from '../context/I18nContext';
import { useToast } from '../context/ToastContext';
import { useRoute, navigate } from '../shared/lib/router';
import {
  listNotifications, markNotificationRead, markAllNotificationsRead,
  getNotificationPreferences, updateNotificationPreferences,
} from '../shared/api/notificationsApi';
import { ENTITY_LINK_PATHS } from '../shared/lib/entityLinks';

function NotificationsFeedTab() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    try {
      const res = await listNotifications({ page: targetPage, pageSize: 20 });
      setItems((prev) => (targetPage === 1 ? res.data : [...prev, ...res.data]));
      setHasMore(targetPage * 20 < res.meta.total);
      setPage(targetPage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1); }, [load]);

  const handleItemClick = async (item) => {
    if (!item.readAt) {
      await markNotificationRead(item.id).catch(() => {});
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, readAt: new Date().toISOString() } : i)));
    }
    const toPath = ENTITY_LINK_PATHS[item.entityType];
    if (toPath && item.entityId) navigate(toPath(item.entityId));
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {});
    setItems((prev) => prev.map((i) => ({ ...i, readAt: i.readAt || new Date().toISOString() })));
  };

  if (loading && items.length === 0) {
    return <p className="empty-state">{t('common', 'actions.loading')}</p>;
  }

  return (
    <div className="tab-panel-content">
      <div className="table-toolbar">
        <button type="button" className="btn btn-secondary" onClick={handleMarkAllRead}>
          {t('notifications', 'feed.markAllRead')}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="empty-state">{t('notifications', 'feed.empty')}</p>
      ) : (
        <ul className="record-list">
          {items.map((item) => (
            <li key={item.id} className="record-list__item record-list__item--clickable" onClick={() => handleItemClick(item)}>
              <div className="record-list__main">
                <span className="record-list__title">
                  {!item.readAt && <span className="badge badge--primary">{t('notifications', 'feed.unreadBadge')}</span>}
                  {item.title}
                </span>
                {item.body && <span className="record-list__subtitle">{item.body}</span>}
                <span className="record-list__meta">{new Date(item.createdAt).toLocaleString()}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="table-toolbar">
          <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => load(page + 1)}>
            {t('notifications', 'feed.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationsPreferencesTab() {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingEventType, setSavingEventType] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotificationPreferences();
      setPreferences(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (pref, field) => {
    setSavingEventType(pref.eventType);
    const updatedPref = { ...pref, [field]: !pref[field] };
    try {
      const res = await updateNotificationPreferences([
        { eventType: updatedPref.eventType, inAppEnabled: updatedPref.inAppEnabled, emailEnabled: updatedPref.emailEnabled },
      ]);
      setPreferences(res.data);
      showToast(t('notifications', 'preferences.saved'));
    } finally {
      setSavingEventType(null);
    }
  };

  if (loading) {
    return <p className="empty-state">{t('common', 'actions.loading')}</p>;
  }

  return (
    <div className="tab-panel-content">
      <p className="form-hint">{t('notifications', 'preferences.intro')}</p>
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('notifications', 'preferences.columnEvent')}</th>
            <th>{t('notifications', 'preferences.columnInApp')}</th>
            <th>{t('notifications', 'preferences.columnEmail')}</th>
          </tr>
        </thead>
        <tbody>
          {preferences.map((pref) => (
            <tr key={pref.eventType}>
              <td>{t('notifications', `eventTypes.${pref.eventType}`)}</td>
              <td>
                <input
                  type="checkbox"
                  checked={pref.inAppEnabled}
                  disabled={savingEventType === pref.eventType}
                  onChange={() => handleToggle(pref, 'inAppEnabled')}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={pref.emailEnabled}
                  disabled={savingEventType === pref.eventType}
                  onChange={() => handleToggle(pref, 'emailEnabled')}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SECTIONS = [
  { key: 'feed', segment: '', labelKey: 'tabs.feed', Component: NotificationsFeedTab },
  { key: 'preferences', segment: 'preferences', labelKey: 'tabs.preferences', Component: NotificationsPreferencesTab },
];

/**
 * Account-level page (like SecuritySettingsPage) -- not gated by any
 * module/permission, since every authenticated user has their own
 * notifications and preferences regardless of which business modules their
 * tenant has enabled. Wired directly in App.jsx before the module lookup.
 */
export default function NotificationsPage() {
  useNamespace('notifications');
  const { t } = useI18n();
  const pathname = useRoute();
  const match = pathname.match(/^\/notifications\/?([^/]*)/);
  const segment = match ? match[1] : '';
  const activeSection = SECTIONS.find((s) => s.segment === segment) || SECTIONS[0];
  const ActiveComponent = activeSection.Component;

  return (
    <div className="page">
      <div className="page__header">
        <h1 className="page__title">{t('notifications', 'title')}</h1>
      </div>

      <div className="tabs">
        <div className="tab-list" role="tablist">
          {SECTIONS.map((section) => (
            <button
              key={section.key}
              type="button"
              role="tab"
              aria-selected={activeSection.key === section.key}
              className={`tab-button${activeSection.key === section.key ? ' tab-button--active' : ''}`}
              onClick={() => navigate(section.segment ? `/notifications/${section.segment}` : '/notifications')}
            >
              {t('notifications', section.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <ActiveComponent />
    </div>
  );
}
